import { ReactNode, useCallback } from 'react';
import { formatPercentage, Flex, Box, ThemeProvider } from '@blockanalitica/ui';
import { Chart, TooltipModel } from 'chart.js';
import ReactDOMServer from 'react-dom/server';
import { theme } from '@/theme';
import { Theme } from '@blockanalitica/ui/package/types/src/theme/ThemeProvider';

function getOrCreateTooltip(chart: Chart, theme: Theme) {
  let tooltipEl = chart.canvas?.parentNode?.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
    tooltipEl.style.borderRadius = theme.sizes['-2'];
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'all .1s ease';

    chart.canvas?.parentNode?.appendChild(tooltipEl);
  }

  return tooltipEl;
}

export default function useChartTooltip(
  data: Record<string, string | number | ReactNode>[] | undefined,
  title: string,
  shareField: string,
  amountField?: string
) {
  const externalTooltipHandler = useCallback(
    (context: {
      chart: Chart;
      tooltip: TooltipModel<'doughnut'>;
      replay: boolean;
    }) => {
      if (!data) {
        return null;
      }

      const { chart, tooltip } = context;
      const tooltipEl = getOrCreateTooltip(chart, theme);

      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = '0';
        return;
      }

      const dataIndex = tooltip.dataPoints[0].dataIndex;

      const content = ReactDOMServer.renderToString(
        <ThemeProvider theme={theme}>
          <Flex flexDirection="column" gap="2xs" padding="xs">
            <Flex
              flexDirection="row"
              justifyContent="flex-start"
              alignContent="center"
              alignItems="center"
              gap="3xs"
              style={{ fontWeight: 'bold' }}
              size="-1">
              <Box
                as="span"
                width={theme.space['xs']}
                height={theme.space['xs']}
                borderRadius="50%"
                backgroundColor={
                  tooltip.labelColors[0].backgroundColor as string
                }
                border={`solid ${tooltip.labelColors[0].borderColor}`}
                borderWidth={theme.space['4xs']}
              />
              {data[dataIndex][title]}
            </Flex>
            <Flex>
              Share: {formatPercentage(Number(data[dataIndex][shareField]))}
            </Flex>

            {amountField ? (
              <Flex>Amount: {data[dataIndex][amountField]}</Flex>
            ) : null}
          </Flex>
        </ThemeProvider>
      );

      tooltipEl.innerHTML = content;
      const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
      tooltipEl.style.opacity = '1';
      tooltipEl.style.left = positionX + tooltip.caretX + 'px';
      tooltipEl.style.top = positionY + tooltip.caretY + 'px';
    },
    [data, title, shareField, amountField]
  );

  return externalTooltipHandler;
}
